/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2020 - 2020
///                            Coyote Logistics L.L.C.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import React from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import { useTheme } from "@material-ui/core/styles";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";

import TrackingNotesIconTooltip from "./common/TrackingNotesIconTooltip";
import AlertsIconWithTooltip from "../components/common/AlertsIconWithTooltip";
import SuperscriptText from "../components/common/SuperscriptText";
import TrackingNotesIcon from "../components/common/TrackingNotesIcon";
import WatchedIcon from "../components/common/WatchedIcon";
import types from "../helpers/types";
import StyledTableCellPinned from "./common/StyledTableCellPinned";
import { resetCarrier } from "../slices/carrierSlice";
import { resetLegs } from "../slices/legsSlice";
import { resetNotes } from "../slices/notesSlice";
import {
  formatUserLocalTime,
  formatTimeWithTimeZone,
} from "../helpers/dateHelpers";

const StyledTableRow = styled(
  ({ selected, openedRoute, row, theme, ...props }) => <TableRow {...props} />
)`
  ${(props) => {
    if (props.selected) {
      return `background-color: ${props.theme.palette.primary.hover} !important;`;
    } else {
      return `
        &:nth-of-type(odd) {
          background-color: ${props.theme.palette.primary.lighter} !important;
        }
      
        &:nth-of-type(even) {
          background-color: white !important;
        }
      
        &:hover {
          cursor: pointer;
          background-color: ${props.theme.palette.primary.hover} !important;
        }
      `;
    }
  }}

  .styled-table-cell {
    text-align: left;
    height: 40px;
    background-color: inherit;

    &.sticky {
      position: sticky !important;
      top: 0;
      z-index: 85;
    }
  }
`;

const StyledTableCell = styled(TableCell)`
  border-bottom: none !important;
`;

const formatTimeSpan = (timeSpan) => {
  let formattedTimeSpan;

  if (timeSpan != null && !timeSpan.startsWith("-") && timeSpan != "00:00:00") {
    // Drops the seconds and milliseconds and
    // keeps the days (if present), hours, and minutes
    // given a d.HH:mm:ss.sssssss formatted string
    formattedTimeSpan = timeSpan.replace(
      /(\d+\.)?(\d{2}:\d{2}):\d{2}(\.\d+)?/,
      "$1$2"
    );

    formattedTimeSpan = (
      <Typography color="error" component="span">
        {formattedTimeSpan}
      </Typography>
    );
  } else {
    formattedTimeSpan = "--";
  }

  return formattedTimeSpan;
};

const getSortType = (sortType) => {
  const key = sortType.replace(/\s+/g, "");
  return types.sortTypes[key];
};

const CoveredRoutesRow = ({
  row,
  selected,
  setOpenedRoute,
  setUnsavedModalOpen,
  unsavedChanges
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const carrierRequestStatus = useSelector(
    (state) => state.carrier.requestStatus
  );
  const legsRequestStatus = useSelector((state) => state.legs.requestStatus);

  const onRowClick = () => {
    if (unsavedChanges) {
      setUnsavedModalOpen(true);
    } else {
      if (
        !selected &&
        (carrierRequestStatus !== types.requestStatuses.inProgress ||
          legsRequestStatus !== types.requestStatuses.inProgress)
      ) {
        dispatch(resetCarrier());
        dispatch(resetLegs());
        dispatch(resetNotes());
        setOpenedRoute(row);
      }
    }
  };

  const getTrailerTooltip = (trailerCapacity, trailerVolume) => {
    // the regex returns as follows:
    // 45 becomes 45
    // 3856 becomes 3,856
    // 39886 becomes 39,886
    // 398867 becomes 398,867
    return (
      <>
        <span>Full: {trailerCapacity}%</span>
        <br />
        <span>
          Vol:{" "}
          {trailerVolume?.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,")}
        </span>
      </>
    );
  };

  const getServiceLevelTooltip = (code) => {
    if (code) {
      return types.serviceLevels.find(
        (serviceLevel) => serviceLevel.code === code
      ).description;
    } else {
      return "";
    }
  };

  const getFacilityTooltip = (facility) => {
    if (facility) {
      return (
        <>
          {facility.name}
          <br />
          {facility.city}, {facility.stateCode}
        </>
      );
    } else {
      return "";
    }
  };

  const getTimeTooltip = (
    time,
    movementOriginTzOffset,
    movementDestinationTzOffset
  ) => {
    if (time) {
      return (
        <>
          <span>
            Origin: {formatTimeWithTimeZone(time, movementOriginTzOffset)}
          </span>
          <br />
          <span>
            Destination:{" "}
            {formatTimeWithTimeZone(time, movementDestinationTzOffset)}
          </span>
          <br />
          <span>User Local: {formatUserLocalTime(time)}</span>
        </>
      );
    } else return "";
  };

  const getLocationDetails = (activeLeg) => {
    let bingMapUrl = "https://bing.com/maps/default.aspx?";
    if (activeLeg.location && activeLeg.movementDestinationFacility) {
      const { address1, city, stateCode, postalCode, latitude, longitude } =
        activeLeg.movementDestinationFacility;
      if (address1 && city && stateCode) {
        bingMapUrl = `${bingMapUrl}rtp=adr.${activeLeg.location}~adr.${address1},${city},${stateCode}`;
      } else if (postalCode && stateCode) {
        bingMapUrl = `${bingMapUrl}rtp=adr.${activeLeg.location}~adr.${postalCode},${stateCode}`;
      } else if (city && stateCode) {
        bingMapUrl = `${bingMapUrl}rtp=adr.${activeLeg.location}~adr.${city},${stateCode}`;
      } else if (latitude && longitude) {
        bingMapUrl = `${bingMapUrl}rtp=adr.${activeLeg.location}~pos.${latitude}_${longitude}`;
      } else {
        return "--";
      }
      return (
        <a
          href={bingMapUrl}
          target="_blank"
          data-testid={`bing-maps-link-${row.id}`}
        >
          {activeLeg.location}
        </a>
      );
    }
    return "--";
  };

  return (
    <StyledTableRow
      hover={true}
      theme={theme}
      tabIndex={-1}
      key={row.code}
      onClick={onRowClick}
      row={row}
      selected={selected}
      data-testid={`covered-route-row-${row.id}`}
    >
      <StyledTableCellPinned
        align="left"
        className="styled-table-cell sticky upsRouteId"
        size="small"
        onClick={(e) => { e.stopPropagation(); }}
      >
        {row.upsRouteId ?? "--"}
      </StyledTableCellPinned>
      <StyledTableCellPinned
        align="left"
        className="styled-table-cell sticky code"
        size="small"
        onClick={(e) => { e.stopPropagation(); }}
      >
        {row.code}-{row.currentLeg}
      </StyledTableCellPinned>
      <StyledTableCellPinned
        align="left"
        className="styled-table-cell sticky leg"
        size="small"
      >
        {row.currentLeg}/{row.totalLegs}
      </StyledTableCellPinned>
      <StyledTableCellPinned
        align="left"
        className="styled-table-cell sticky trailer"
        size="small"
        onClick={(e) => { e.stopPropagation(); }}
      >
        <Tooltip
          title={getTrailerTooltip(row.trailerCapacity, row.trailerVolume)}
        >
          <span>{row.trailer}</span>
        </Tooltip>
      </StyledTableCellPinned>
      <StyledTableCellPinned
        align="left"
        className="styled-table-cell sticky srv"
        size="small"
      >
        <Tooltip title={getServiceLevelTooltip(row.serviceLevel)}>
          <span>{row.serviceLevel}</span>
        </Tooltip>
      </StyledTableCellPinned>
      <StyledTableCellPinned
        align="right"
        className="styled-table-cell sticky miles"
        style={{ textAlign: "right" }}
        size="small"
      >
        {row.miles}
      </StyledTableCellPinned>
      <StyledTableCellPinned
        align="left"
        className="styled-table-cell small-cell sticky notes"
        size="small"
      >
        {
          (row.noteReadStatus === types.noteReadStatus.read ||
            row.noteReadStatus === types.noteReadStatus.unread) &&
          <TrackingNotesIconTooltip noteReadStatus={row.noteReadStatus} legId={row.activeLeg.legsId} />
        }
      </StyledTableCellPinned>
      <StyledTableCellPinned
        align="left"
        className="styled-table-cell small-cell sticky alerts"
        size="small"
      >
        <AlertsIconWithTooltip
          alerts={row.alerts}
          style={{ display: row.alerts.length ? "inline" : "none" }}
        />
      </StyledTableCellPinned>
      <StyledTableCellPinned
        align="left"
        className="styled-table-cell small-cell sticky watched"
        size="small"
      >
        <WatchedIcon style={{ display: row.watched ? "inline" : "none" }} />
      </StyledTableCellPinned>
      <StyledTableCellPinned
        align="left"
        className="styled-table-cell sticky carrier"
        size="small"
      >
        {row.carrierName}
      </StyledTableCellPinned>
      <StyledTableCell align="left" className="styled-table-cell" size="small">
        <Grid container>
          <Grid item xs={12}>
            <Tooltip
              title={getFacilityTooltip(row.activeLeg.loadOriginFacility)}
            >
              <span>
                <span style={{ fontWeight: 500 }}>Load: </span>
                {row.activeLeg.loadOriginFacility?.slicCode
                  ? row.activeLeg.loadOriginFacility.slicCode
                  : "--"}
                <SuperscriptText>
                  {row.activeLeg.originSortType === "Unknown"
                    ? "--"
                    : getSortType(row.activeLeg.originSortType).code}
                </SuperscriptText>
              </span>
            </Tooltip>
          </Grid>
          <Grid item xs={12}>
            <Tooltip
              title={getFacilityTooltip(row.activeLeg.movementOriginFacility)}
            >
              <span>
                <span style={{ fontWeight: 500 }}>Mvmnt: </span>
                {row.activeLeg.movementOriginFacility?.alphaCode
                  ? row.activeLeg.movementOriginFacility.alphaCode
                  : "--"}
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      </StyledTableCell>
      <StyledTableCell align="left" className="styled-table-cell" size="small">
        <Grid container>
          <Grid item xs={12}>
            <Tooltip
              title={getFacilityTooltip(row.activeLeg.loadDestinationFacility)}
            >
              <span>
                <span style={{ fontWeight: 500 }}>Load: </span>
                {row.activeLeg.loadDestinationFacility?.slicCode
                  ? row.activeLeg.loadDestinationFacility.slicCode
                  : "--"}
                <SuperscriptText>
                  {row.activeLeg.destinationSortType === "Unknown"
                    ? "--"
                    : getSortType(row.activeLeg.destinationSortType).code}
                </SuperscriptText>
              </span>
            </Tooltip>
          </Grid>
          <Grid item xs={12}>
            <Tooltip
              title={getFacilityTooltip(
                row.activeLeg.movementDestinationFacility
              )}
            >
              <span>
                <span style={{ fontWeight: 500 }}>Mvmnt: </span>
                {row.activeLeg.movementDestinationFacility?.alphaCode
                  ? row.activeLeg.movementDestinationFacility.alphaCode
                  : "--"}
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      </StyledTableCell>
      <StyledTableCell align="left" className="styled-table-cell" size="small">
        <Grid container>
          <Grid container item xs={12}>
            <Grid item xs={6}>
              <Tooltip
                title={getTimeTooltip(
                  row.activeLeg.startTimeScheduled,
                  row.activeLeg.movementOriginFacility?.timeZoneName,
                  row.activeLeg.movementDestinationFacility?.timeZoneName
                )}
              >
                <span>
                  <span style={{ fontWeight: 500 }}>Sched: </span>
                  {row.activeLeg.startTimeScheduled
                    ? formatTimeWithTimeZone(
                      row.activeLeg.startTimeScheduled,
                      row.activeLeg.movementOriginFacility?.timeZoneName
                    )
                    : "--"}
                </span>
              </Tooltip>
            </Grid>
            <Grid item xs={6}>
              <Tooltip
                title={getTimeTooltip(
                  row.activeLeg.startTimeActual,
                  row.activeLeg.movementOriginFacility?.timeZoneName,
                  row.activeLeg.movementDestinationFacility?.timeZoneName
                )}
              >
                <span>
                  <span style={{ fontWeight: 500 }}>Actual: </span>
                  {row.activeLeg.startTimeActual
                    ? formatTimeWithTimeZone(
                      row.activeLeg.startTimeActual,
                      row.activeLeg.movementOriginFacility?.timeZoneName
                    )
                    : "--"}
                </span>
              </Tooltip>
            </Grid>
          </Grid>
          <Grid container item xs={12}>
            <Grid item xs={6}>
              <span style={{ fontWeight: 500 }}>Late: </span>
              {formatTimeSpan(row.activeLeg.startTimeLate)}
            </Grid>
          </Grid>
        </Grid>
      </StyledTableCell>
      <StyledTableCell align="left" className="styled-table-cell" size="small">
        <Grid container>
          <Grid container item xs={12}>
            <Grid item xs={6}>
              <Tooltip
                title={getTimeTooltip(
                  row.activeLeg.lastContactTime,
                  row.activeLeg.movementOriginFacility?.timeZoneName,
                  row.activeLeg.movementDestinationFacility?.timeZoneName
                )}
              >
                <span>
                  <span style={{ fontWeight: 500 }}>Time: </span>
                  {row.activeLeg.lastContactTime
                    ? formatUserLocalTime(row.activeLeg.lastContactTime)
                    : "--"}
                </span>
              </Tooltip>
            </Grid>
            <Grid item xs={6}>
              <span style={{ fontWeight: 500 }}>Rmn: </span>
              {row.activeLeg.milesRemainingAtLastContact
                ? row.activeLeg.milesRemainingAtLastContact
                : "--"}
            </Grid>
          </Grid>
          <Grid container item xs={12}>
            <Grid item xs={6}>
              <span style={{ fontWeight: 500 }}>Silent: </span>
              {formatTimeSpan(row.activeLeg.lastContactSilent)}
            </Grid>
            <Grid item xs={6}>
              <span style={{ fontWeight: 500 }}>Location: </span>
              <span
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                {getLocationDetails(row.activeLeg)}
              </span>
            </Grid>
          </Grid>
        </Grid>
      </StyledTableCell>
      <StyledTableCell align="left" className="styled-table-cell" size="small">
        <Grid container>
          <Grid container item xs={12}>
            <Grid item xs={6}>
              <Tooltip
                title={getTimeTooltip(
                  row.activeLeg.endTimeScheduled,
                  row.activeLeg.movementOriginFacility?.timeZoneName,
                  row.activeLeg.movementDestinationFacility?.timeZoneName
                )}
              >
                <span>
                  <span style={{ fontWeight: 500 }}>Sched: </span>
                  {row.activeLeg.endTimeScheduled
                    ? formatTimeWithTimeZone(
                      row.activeLeg.endTimeScheduled,
                      row.activeLeg.movementDestinationFacility?.timeZoneName
                    )
                    : "--"}
                </span>
              </Tooltip>
            </Grid>
            <Grid item xs={6}>
              <Tooltip
                title={getTimeTooltip(
                  row.activeLeg.endTimeEta,
                  row.activeLeg.movementOriginFacility?.timeZoneName,
                  row.activeLeg.movementDestinationFacility?.timeZoneName
                )}
              >
                <span>
                  <span style={{ fontWeight: 500 }}>ETA: </span>
                  {row.activeLeg.endTimeEta
                    ? formatTimeWithTimeZone(
                      row.activeLeg.endTimeEta,
                      row.activeLeg.movementDestinationFacility?.timeZoneName
                    )
                    : "--"}
                </span>
              </Tooltip>
            </Grid>
          </Grid>
          <Grid container item xs={12}>
            <Grid item xs={6}>
              <span style={{ fontWeight: 500 }}>Late: </span>
              {formatTimeSpan(row.activeLeg.endTimeLate)}
            </Grid>
          </Grid>
        </Grid>
      </StyledTableCell>
      <StyledTableCell align="left" className="styled-table-cell" size="small">
        <Grid container>
          <Grid container item xs={12}>
            <Grid item xs={12}>
              <Tooltip
                title={getTimeTooltip(
                  row.activeLeg.sortCutoff,
                  row.activeLeg.movementOriginFacility?.timeZoneName,
                  row.activeLeg.movementDestinationFacility?.timeZoneName
                )}
              >
                <span>
                  <span style={{ fontWeight: 500 }}>Cutoff: </span>
                  {row.activeLeg.sortCutoff
                    ? formatTimeWithTimeZone(
                      row.activeLeg.sortCutoff,
                      row.activeLeg.movementDestinationFacility?.timeZoneName
                    )
                    : "--"}
                </span>
              </Tooltip>
            </Grid>
          </Grid>
          <Grid container item xs={12}>
            <Grid item xs>
              <span style={{ fontWeight: 500 }}>Late: </span>
              {row.activeLeg.sortLate
                ? formatTimeSpan(row.activeLeg.sortLate)
                : "--"}
            </Grid>
          </Grid>
        </Grid>
      </StyledTableCell>
      <StyledTableCell align="left" className="styled-table-cell" size="small">
        <Grid container>
          <Grid container item xs={12}>
            <Grid item xs={6}>
              <Tooltip
                title={getTimeTooltip(
                  row.nextLeg?.startTimeScheduledUtc,
                  row.nextLeg?.originFacilityTimeZoneName,
                  row.nextLeg?.destinationFacilityTimeZoneName
                )}
              >
                <span>
                  <span style={{ fontWeight: 500 }}>Start: </span>
                  {row.nextLeg?.startTimeScheduledUtc
                    ? formatTimeWithTimeZone(
                      row.nextLeg.startTimeScheduledUtc,
                      row.nextLeg?.originFacilityTimeZoneName
                    )
                    : "--"}
                </span>
              </Tooltip>
            </Grid>
            <Grid item xs={6}>
              <span style={{ fontWeight: 500 }}>Miles: </span>
              {row.nextLeg?.miles ? row.nextLeg.miles : "--"}
            </Grid>
          </Grid>
          <Grid container item xs={12}>
            <Grid item xs={6}>
              <span style={{ fontWeight: 500 }}>Late: </span>
              {formatTimeSpan(row.nextLeg?.endTimeLate)}
            </Grid>
            <Grid item xs={6}>
              <span style={{ fontWeight: 500 }}>Srv: </span>
              {row.nextLeg?.srv ? (
                <Tooltip title={getServiceLevelTooltip(row.nextLeg.srv)}>
                  <span>{row.nextLeg.srv}</span>
                </Tooltip>
              ) : (
                "--"
              )}
            </Grid>
          </Grid>
        </Grid>
      </StyledTableCell>
    </StyledTableRow>
  );
};

CoveredRoutesRow.propTypes = {
  row: PropTypes.object.isRequired,
  setOpenedRoute: PropTypes.func.isRequired,
  setUnsavedModalOpen: PropTypes.func.isRequired,
  unsavedChanges: PropTypes.bool.isRequired,
};

export default CoveredRoutesRow;
